import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DEFAULT_MENU, ICON_MAP } from '../constants';
import { ChevronRight } from 'lucide-react';
import { useConfig } from '../hooks/useConfig';
import { useAuth } from '../hooks/useAuth';
import { useMenu } from '../hooks/useMenu';
import { SignedImage } from '../components/SignedImage';
import { LogoutModal } from '../components/LogoutModal';

const CategoryTile: React.FC<{ child: any; styles: any; getTileStyles: any; onLogoutRequest: () => void }> = ({ child, styles, getTileStyles, onLogoutRequest }) => {
  const [imgError, setImgError] = useState(false);
  const itemStyles = getTileStyles(child.item);
  const IconFromMenu = child.icon ? ICON_MAP[child.icon] : null;
  const Icon = itemStyles.icon ? ICON_MAP[itemStyles.icon] : IconFromMenu;
  const iconPath = itemStyles.iconPath || child.iconPath;
  
  const isLogout = child.item === 'Uscita' || child.route === '/logout';

  const handleClick = (e: React.MouseEvent) => {
    if (isLogout) {
      e.preventDefault();
      onLogoutRequest();
    }
  };

  return (
    <Link
      to={isLogout ? '#' : (child.route || '#')}
      onClick={handleClick}
      title="Apri sezione"
      className="group flex flex-col items-center justify-center border hover:shadow-lg transition-all text-center rounded-2xl p-4 w-[var(--w-mobile)] h-[var(--h-mobile)] lg:w-[var(--w-desktop)] lg:h-[var(--h-desktop)] md:w-[var(--w-tablet)] md:h-[var(--h-tablet)] bg-[var(--bg-mobile)] md:bg-[var(--bg-tablet)] lg:bg-[var(--bg-desktop)]"
      style={{ 
        borderColor: itemStyles.bordo_sezione_colore || itemStyles.borderColor,
        borderWidth: itemStyles.bordo_sezione_spessore || itemStyles.borderSize,
        ['--w-desktop' as any]: itemStyles.desktop_larghezza,
        ['--h-desktop' as any]: itemStyles.desktop_altezza,
        ['--bg-desktop' as any]: itemStyles.desktop_sfondo,
        ['--w-tablet' as any]: itemStyles.tablet_larghezza,
        ['--h-tablet' as any]: itemStyles.tablet_altezza,
        ['--bg-tablet' as any]: itemStyles.tablet_sfondo,
        ['--w-mobile' as any]: itemStyles.larghezza_mobile,
        ['--h-mobile' as any]: itemStyles.altezza_mobile,
        ['--bg-mobile' as any]: itemStyles.mobile_sfondo,
        margin: itemStyles.margin
      }}
    >
      <div className="relative mb-2 flex items-center justify-center w-full">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden shrink-0 border"
          style={{ 
            backgroundColor: itemStyles.sfondo_icona || 'rgba(59, 130, 246, 0.1)',
            color: itemStyles.colore_icona || '#3b82f6',
            borderColor: itemStyles.bordo_icona_colore || '#ffffff',
            borderWidth: itemStyles.bordo_icona_spessore || '1px'
          }}
        >
          {iconPath && !imgError ? (
            <SignedImage 
              path={iconPath} 
              bucket="cpgev"
              alt="" 
              className="w-6 h-6 object-contain"
              onError={() => setImgError(true)}
            />
          ) : (
            Icon && <Icon className="w-6 h-6" />
          )}
        </div>
        {child.children && child.children.length > 0 && (
          <span 
            className="absolute left-[calc(50%+25px)] text-sm font-black font-mono select-none"
            style={{ color: itemStyles.colore_icona || '#3b82f6' }}
          >
            =&gt;
          </span>
        )}
      </div>
      <h3 
        className="text-sm line-clamp-2 px-1 leading-tight flex-grow flex items-center justify-center transition-colors"
      >
        <span 
          className="hidden lg:block w-full" 
          style={{ 
            color: itemStyles.desktop_colore, 
            fontFamily: itemStyles.desktop_font, 
            fontWeight: itemStyles.desktop_stile?.includes('bold') ? '700' : '400',
            fontStyle: itemStyles.desktop_stile?.includes('italic') ? 'italic' : 'normal'
          }}
        >
          {child.item}
        </span>
        <span 
          className="hidden md:block lg:hidden w-full" 
          style={{ 
            color: itemStyles.tablet_colore, 
            fontFamily: itemStyles.tablet_font, 
            fontWeight: itemStyles.tablet_stile?.includes('bold') ? '700' : '400',
            fontStyle: itemStyles.tablet_stile?.includes('italic') ? 'italic' : 'normal'
          }}
        >
          {child.item}
        </span>
        <span 
          className="md:hidden w-full" 
          style={{ 
            color: itemStyles.mobile_colore, 
            fontFamily: itemStyles.mobile_font, 
            fontWeight: itemStyles.mobile_stile?.includes('bold') ? '700' : '400',
            fontStyle: itemStyles.mobile_stile?.includes('italic') ? 'italic' : 'normal'
          }}
        >
          {child.item}
        </span>
      </h3>
    </Link>
  );
};

export const CategoryPage: React.FC = () => {
  const location = useLocation();
  const path = location.pathname;
  const { getTileStyles } = useConfig();
  const { user, signOut } = useAuth();
  const { menu } = useMenu();
  const styles = getTileStyles();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleLogout = async () => {
    setIsLogoutModalOpen(false);
    await signOut();
  };
  
  // Find the current menu item based on the route (recursive search)
  const findMenuItem = (items: any[], route: string): any => {
    for (const item of items) {
      if (item.route === route) return item;
      if (item.children) {
        const found = findMenuItem(item.children, route);
        if (found) return found;
      }
    }
    return null;
  };

  const currentItem = findMenuItem(menu, path);
  
  if (!currentItem || !currentItem.children) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <h2 className="text-2xl font-bold mb-2">Pagina non trovata</h2>
        <p className="text-muted-foreground">La categoria richiesta non esiste o non ha sottomenu.</p>
        <Link to="/user/dashboard" className="mt-4 text-primary hover:underline">Torna alla Dashboard</Link>
      </div>
    );
  }

  // Filter children based on user groups/permissions
  const filteredChildren = currentItem.children.filter(child => {
    if (user?.role === 'Admin') return true;
    
    // Normalization function for group matching
    const normalize = (name: string) => name.toLowerCase().replace(/[._\-\s]/g, '');
    
    const accessKey = child.auth?.view || child.permission_key || child.permissionKey || child.requiredGroup;
    
    if (accessKey) {
      const keyNorm = normalize(accessKey);
      return user?.groups.some(group => {
        const groupNorm = normalize(group.name);
        return groupNorm === keyNorm || 
               groupNorm === keyNorm.replace(/^app/, '') ||
               keyNorm === groupNorm.replace(/^app/, '') ||
               groupNorm === keyNorm.replace(/\./g, '') ||
               groupNorm === keyNorm.replace(/^app\./, '').replace(/\./g, '');
      }) || false;
    }

    return true; // If no access key specified, assume visible
  });

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemAnim = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{currentItem.item}</h1>
        <p className="text-muted-foreground">Seleziona una sezione per procedere.</p>
      </div>

      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="flex flex-wrap"
        style={{ gap: styles.margin }}
      >
        {filteredChildren.map((child, idx) => (
          <motion.div key={idx} variants={itemAnim}>
            <CategoryTile child={child} styles={styles} getTileStyles={getTileStyles} onLogoutRequest={() => setIsLogoutModalOpen(true)} />
          </motion.div>
        ))}
      </motion.div>

      <LogoutModal 
        isOpen={isLogoutModalOpen} 
        onClose={() => setIsLogoutModalOpen(false)} 
        onConfirm={handleLogout} 
      />
    </div>
  );
};
